/* ============================================================
   L'ART DU CARRÉ — Recherche instantanée
   Fichier : assets/fc-search-instant.js
   ------------------------------------------------------------
   Panneau de suggestions ouvert depuis l'icône loupe : produits
   (vignette, titre, prix, disponibilité), collections, requêtes
   suggérées, recherches récentes. Aucun changement de page tant
   que le visiteur n'a pas choisi.
   Autonome : ne dépend d'aucune classe du thème.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('fc-search')) return;

  var ENDPOINT = '/search/suggest.json';
  var STORE_KEY = 'fc:recent-searches';
  var MAX_RECENT = 5;

  function money(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var locale = document.documentElement.lang || 'fr-FR';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(n);
    } catch (e) {
      return n.toFixed(2).replace('.', ',') + ' €';
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function highlight(text, term) {
    var safe = esc(text);
    if (!term) return safe;
    var needle = term.trim().split(/\s+/).filter(Boolean).map(function (w) {
      return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }).join('|');
    if (!needle) return safe;
    try {
      return safe.replace(new RegExp('(' + needle + ')', 'gi'), '<mark>$1</mark>');
    } catch (e) { return safe; }
  }

  function thumb(url, width) {
    if (!url) return '';
    return url.indexOf('?') > -1
      ? url + '&width=' + width
      : url + '?width=' + width;
  }

  function readRecent() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '[]'); } catch (e) { return []; }
  }
  function pushRecent(term) {
    if (!term) return;
    try {
      var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
    } catch (e) {}
  }

  class FCSearch extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-fcs-trigger]');
      this.overlay = this.querySelector('[data-fcs-overlay]');
      if (!this.trigger || !this.overlay) return;

      // Sortir le panneau du header collant pour éviter tout rognage.
      document.body.appendChild(this.overlay);

      this.form = this.overlay.querySelector('form');
      this.input = this.overlay.querySelector('[data-fcs-input]');
      this.results = this.overlay.querySelector('[data-fcs-results]');
      this.status = this.overlay.querySelector('[data-fcs-status]');
      this.clearBtn = this.overlay.querySelector('[data-fcs-clear]');
      this.idle = this.overlay.querySelector('[data-fcs-idle]');
      this.recentWrap = this.overlay.querySelector('[data-fcs-recent]');

      this.limit = parseInt(this.getAttribute('data-limit'), 10) || 6;
      this.searchUrl = this.getAttribute('data-search-url') || '/search';
      this.cache = new Map();
      this.controller = null;
      this.activeIndex = -1;
      this.debounce = null;

      this.trigger.addEventListener('click', this.open.bind(this));
      this.overlay.addEventListener('click', function (e) {
        if (e.target === this.overlay) this.close();
      }.bind(this));
      this.overlay.querySelectorAll('[data-fcs-close]').forEach(function (b) {
        b.addEventListener('click', this.close.bind(this));
      }.bind(this));

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.clearBtn.addEventListener('click', this.clear.bind(this));
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && this.isOpen) this.close();
      }.bind(this));

      // Raccourci clavier : « / » ouvre la recherche.
      document.addEventListener('keydown', function (e) {
        if (e.key !== '/' || this.isOpen) return;
        var t = e.target;
        if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
        if (t && t.isContentEditable) return;
        e.preventDefault();
        this.open();
      }.bind(this));

      this.overlay.addEventListener('click', function (e) {
        var link = e.target.closest('a[data-fcs-term]');
        if (link) pushRecent(link.getAttribute('data-fcs-term'));
        var chip = e.target.closest('[data-fcs-fill]');
        if (chip) {
          e.preventDefault();
          this.input.value = chip.getAttribute('data-fcs-fill');
          this.input.focus();
          this.onInput();
        }
      }.bind(this));
    }

    get isOpen() { return this.overlay && !this.overlay.hidden; }

    open(e) {
      if (e) e.preventDefault();
      this.overlay.hidden = false;
      document.documentElement.classList.add('fcs-locked');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.renderRecent();
      // Reflow forcé plutôt que requestAnimationFrame : la transition démarre et
      // le champ reçoit le focus dans la même tâche que le clic — indispensable
      // pour que le clavier s'ouvre sur iOS, et fiable si l'onglet est caché.
      void this.overlay.offsetWidth;
      this.overlay.classList.add('is-open');
      this.input.focus();
      this.input.select();
    }

    close() {
      this.overlay.classList.remove('is-open');
      document.documentElement.classList.remove('fcs-locked');
      this.trigger.setAttribute('aria-expanded', 'false');
      var hide = function () { this.overlay.hidden = true; }.bind(this);
      setTimeout(hide, 180);
      this.trigger.focus();
    }

    clear() {
      this.input.value = '';
      this.clearBtn.hidden = true;
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.idle) this.idle.hidden = false;
      this.renderRecent();
      this.input.focus();
    }

    onSubmit(e) {
      var term = this.input.value.trim();
      if (!term) { e.preventDefault(); return; }
      pushRecent(term);
    }

    onInput() {
      var term = this.input.value.trim();
      this.clearBtn.hidden = !term;
      clearTimeout(this.debounce);

      if (term.length < 2) {
        this.results.hidden = true;
        this.results.innerHTML = '';
        if (this.idle) this.idle.hidden = false;
        this.setStatus('');
        return;
      }
      if (this.idle) this.idle.hidden = true;
      this.overlay.classList.add('is-loading');
      this.debounce = setTimeout(this.fetchResults.bind(this, term), 180);
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }

    fetchResults(term) {
      if (this.cache.has(term)) {
        this.render(this.cache.get(term), term);
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      var params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', 'product,collection,query');
      params.set('resources[limit]', String(this.limit));
      params.set('resources[options][unavailable_products]', 'last');

      fetch(ENDPOINT + '?' + params.toString(), { signal: this.controller.signal })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var res = (data && data.resources && data.resources.results) || {};
          this.cache.set(term, res);
          this.render(res, term);
        }.bind(this))
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          this.overlay.classList.remove('is-loading');
          this.results.hidden = false;
          this.results.innerHTML =
            '<p class="fcs__empty">La recherche est momentanément indisponible. ' +
            '<a href="' + esc(this.searchUrl) + '?q=' + encodeURIComponent(term) + '">Voir la page résultats</a></p>';
        }.bind(this));
    }

    render(res, term) {
      this.overlay.classList.remove('is-loading');
      var products = res.products || [];
      var collections = res.collections || [];
      var queries = res.queries || [];
      var html = '';

      if (queries.length) {
        html += '<div class="fcs__group"><p class="fcs__group-title">Suggestions</p><ul class="fcs__chips">';
        queries.slice(0, 5).forEach(function (q) {
          html += '<li><a class="fcs__chip" data-fcs-term="' + esc(q.text) + '" href="' +
            esc(this.searchUrl) + '?q=' + encodeURIComponent(q.text) + '">' +
            highlight(q.text, term) + '</a></li>';
        }.bind(this));
        html += '</ul></div>';
      }

      if (products.length) {
        html += '<div class="fcs__group"><p class="fcs__group-title">Produits</p><ul class="fcs__list">';
        products.forEach(function (p) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          var compare = parseFloat(p.compare_at_price_max || '0');
          var price = parseFloat(p.price || p.price_min || '0');
          html +=
            '<li><a class="fcs__item" role="option" data-fcs-term="' + esc(term) + '" href="' + esc(p.url) + '">' +
              '<span class="fcs__thumb">' +
                (img
                  ? '<img src="' + esc(thumb(img, 160)) + '" alt="" loading="lazy" width="80" height="80">'
                  : '<span class="fcs__thumb-empty" aria-hidden="true"></span>') +
              '</span>' +
              '<span class="fcs__item-body">' +
                '<span class="fcs__item-title">' + highlight(p.title, term) + '</span>' +
                '<span class="fcs__item-meta">' +
                  '<span class="fcs__price">' + money(price) + '</span>' +
                  (compare > price ? '<s class="fcs__price-was">' + money(compare) + '</s>' : '') +
                  (p.available === false ? '<span class="fcs__oos">Épuisé</span>' : '') +
                '</span>' +
              '</span>' +
              '<svg class="fcs__go" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>' +
            '</a></li>';
        });
        html += '</ul></div>';
      }

      if (collections.length) {
        html += '<div class="fcs__group"><p class="fcs__group-title">Collections</p><ul class="fcs__chips">';
        collections.slice(0, 6).forEach(function (c) {
          html += '<li><a class="fcs__chip fcs__chip--collection" href="' + esc(c.url) + '">' +
            highlight(c.title, term) + '</a></li>';
        });
        html += '</ul></div>';
      }

      if (!products.length && !collections.length && !queries.length) {
        html =
          '<div class="fcs__empty">' +
            '<p class="fcs__empty-title">Aucun résultat pour « ' + esc(term) + ' »</p>' +
            '<p class="fcs__empty-help">Essayez un mot plus court : <em>soie</em>, <em>carré</em>, <em>cachemire</em>, ou une couleur.</p>' +
          '</div>';
      } else {
        html +=
          '<a class="fcs__all" data-fcs-term="' + esc(term) + '" href="' + esc(this.searchUrl) +
          '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last">' +
          'Voir tous les résultats pour « ' + esc(term) + ' »' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
          '</a>';
      }

      this.results.innerHTML = html;
      this.results.hidden = false;
      this.activeIndex = -1;
      this.setStatus(
        products.length || collections.length
          ? products.length + ' produit' + (products.length > 1 ? 's' : '') + ' trouvé' + (products.length > 1 ? 's' : '')
          : 'Aucun résultat'
      );
    }

    renderRecent() {
      if (!this.recentWrap) return;
      var list = readRecent();
      if (!list.length) { this.recentWrap.hidden = true; return; }
      this.recentWrap.hidden = false;
      this.recentWrap.innerHTML =
        '<p class="fcs__group-title">Vos dernières recherches</p><ul class="fcs__chips">' +
        list.map(function (t) {
          return '<li><button type="button" class="fcs__chip" data-fcs-fill="' + esc(t) + '">' + esc(t) + '</button></li>';
        }).join('') + '</ul>';
    }

    onKeydown(e) {
      var items = Array.prototype.slice.call(this.results.querySelectorAll('.fcs__item, .fcs__chip, .fcs__all'));
      if (!items.length) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex += e.key === 'ArrowDown' ? 1 : -1;
        if (this.activeIndex < 0) this.activeIndex = items.length - 1;
        if (this.activeIndex >= items.length) this.activeIndex = 0;
        items.forEach(function (el, i) {
          el.classList.toggle('is-active', i === this.activeIndex);
          if (i === this.activeIndex) el.scrollIntoView({ block: 'nearest' });
        }.bind(this));
      } else if (e.key === 'Enter' && this.activeIndex > -1) {
        e.preventDefault();
        items[this.activeIndex].click();
      }
    }
  }

  customElements.define('fc-search', FCSearch);
})();